import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { DashboardType } from '~/enums/DashboardType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { preSigMocks } from '~/redux/api/methods/dashboardV2';
import { coerceRtkqError } from '~/redux/api/utils';
import { useRouting } from '~/routing/useRouting';
import { Dashboard as DashboardObjectType } from '~/types/Dashboard';
import { ERROR } from '~/types/toast.types';

import { useCurrentUser } from './useCurrentUser';
import { usePermission } from './usePermission';

export const useDashboardsSubnavs = () => {
  const intl = useIntl();
  const { location } = useRouting();
  const currentUser = useCurrentUser();
  const hasDashboardV3 = useFlag(FlagType.DashboardV3);
  const { pathname } = location;

  const {
    isLoading: isWorkflowDashboardPermissionLoading,
    hasPermission: hasWorkflowDashboardPermission,
  } = usePermission({
    permission: { resourceId: 'workflows_dashboard', resourceType: 'view' },
  });
  const {
    isLoading: isDocDashboardPermissionLoading,
    hasPermission: hasDocDashboardPermission,
  } = usePermission({
    permission: { resourceId: 'documents_dashboard', resourceType: 'view' },
  });
  const [loadLegacyDashboards, legacyDashboardsResults] =
    api.endpoints.getDashboards.useLazyQuery(undefined);
  const { data: legacyDashboards = [] } = legacyDashboardsResults;

  const [
    getDashboardsV2,
    {
      data: dashboardsResponse,
      isError: isFetchingDashboardsErrorV2,
      isSuccess: isFetchingDashboardsSuccessV2,
    },
  ] = api.endpoints.getDashboardsV2.useLazyQuery();

  const [createDashboards, createDefaultDashboardsResult] =
    api.endpoints.createDefaultDashboards.useLazyQuery(undefined);
  const {
    data: defaultDashboardsResponse,
    error,
    isSuccess: isCreatingDefaultDashboardsSuccess,
    isError: isCreatingDefaultDashboardsError,
  } = createDefaultDashboardsResult;

  const hasDefaultDashboards = dashboardsResponse?.results.some(
    (d: any) => d.is_default,
  );

  const isLoadingPermissions =
    isWorkflowDashboardPermissionLoading || isDocDashboardPermissionLoading;

  const hasDocumentDashboardPermission =
    !isDocDashboardPermissionLoading && hasDocDashboardPermission;

  const isDefaultDashboardsSet = !hasDocumentDashboardPermission
    ? true
    : hasDefaultDashboards ||
      isFetchingDashboardsErrorV2 ||
      (defaultDashboardsResponse?.length && isCreatingDefaultDashboardsSuccess);

  const isFetching = isLoadingPermissions || !isDefaultDashboardsSet;

  const checkIfLegacyDashboard = (i: DashboardObjectType) =>
    i['type'] === ('tickets' as DashboardType) ||
    i['type'] === ('workflows' as DashboardType);

  const preSigDashboards =
    hasDashboardV3 && hasWorkflowDashboardPermission
      ? preSigMocks(currentUser, intl)
      : legacyDashboards.filter(checkIfLegacyDashboard);

  useEffect(() => {
    if (
      pathname.startsWith('/dashboards') ||
      pathname.startsWith('/saved-dashboards')
    ) {
      // still need to load legacy dashboards for tickets and workflows tabs uuid
      if (!hasDashboardV3) {
        loadLegacyDashboards(undefined);
      }
      getDashboardsV2({ page: 1, pageSize: 2, return_defaults: true });
    }
  }, [pathname]);

  useEffect(() => {
    if (
      hasDocumentDashboardPermission &&
      !!isFetchingDashboardsSuccessV2 &&
      dashboardsResponse?.results.length === 0
    ) {
      createDashboards(undefined);
    }
  }, [
    hasDocumentDashboardPermission,
    isFetchingDashboardsSuccessV2,
    dashboardsResponse,
    createDashboards,
  ]);

  useEffect(() => {
    if (isCreatingDefaultDashboardsSuccess && !!defaultDashboardsResponse) {
      getDashboardsV2({ page: 1, pageSize: 2, return_defaults: true });
    }
  }, [
    isCreatingDefaultDashboardsSuccess,
    defaultDashboardsResponse,
    getDashboardsV2,
  ]);

  useEffect(() => {
    if (isCreatingDefaultDashboardsError) {
      showToast(
        ERROR,
        `${coerceRtkqError(error)?.response?.data?.errors?.[0]?.title}` ||
          intl.formatMessage({
            id: 'useDashboardsSubnavs.errorCreatingDashboard',
            defaultMessage: 'An error occurs when creating a dashboard.',
            description: 'Error message when dashboard creation fails',
          }),
      );
    }
  }, [isCreatingDefaultDashboardsError, error, intl]);

  const dashboards = [
    ...(dashboardsResponse?.results || []),
    ...preSigDashboards,
  ];

  return {
    dashboards,
    isFetching,
  };
};
